import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Coins, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { declararArrastres, fetchArrastres } from "@/routes/accountant/arrastresApi"
import { fetchSociedades } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

/**
 * LOS SALDOS QUE VIENEN DEL PERIODO ANTERIOR.
 *
 * POR QUE ESTA PANTALLA EXISTE. El FV 621 pide cinco casillas que este sistema
 * no puede calcular: saldo a favor del IGV, retenciones, percepciones, credito
 * por ITAN y saldo a favor de renta. Hasta que alguien las declara **salen en
 * cero**, y un cero ahi no descuadra nada: el formulario sale completo,
 * cuadrado, presentable — y el contribuyente paga de mas.
 *
 * LA DISTINCION QUE SOSTIENE TODO: cero declarado no es cero por falta de dato.
 * Si la empresa de verdad arranca de cero, eso se DECLARA como apertura y la
 * advertencia desaparece. Sin poder decirlo, la advertencia saldria en todas
 * las declaraciones para siempre y acabaria ignorandose — justo el dia que si
 * importe.
 *
 * SE PIDEN LOS CINCO A LA VEZ porque asi llega el dato de verdad: alguien tiene
 * delante la declaracion del mes anterior y copia sus casillas. Pedirlo de uno
 * en uno son cinco formularios para leer un solo papel.
 */
export function ArrastresPage() {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const q = useQuery({ queryKey: ["arrastres"], queryFn: () => fetchArrastres() })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Declaraciones"
        title={t("Saldos arrastrados")}
        description={t("Lo que viene del período anterior y el sistema no puede calcular: hasta que se declara, esas casillas salen en cero y eso no descuadra nada.")}
      />

      {puedeEditar ? (
        <Declarar
          sociedades={sociedades.data ?? []}
          conceptos={q.data?.conceptos ?? []}
        />
      ) : null}

      {(q.data?.faltantes.length ?? 0) > 0 ? (
        <WorkPanel
          title={t("Períodos que hoy declararían cero")}
          meta={
            <Badge variant="warning-soft" estado>
              {q.data!.faltantes.length}
            </Badge>
          }
          footnote={t("Tienen comprobantes y ningún saldo declarado. Su declaración saldría con las cinco casillas en cero — que es legal, presentable, y puede estar pagando de más.")}
          bodyClassName="p-2.5"
        >
          <p className="text-2xs leading-5">
            {q.data!.faltantes.slice(0, 18).map((f) => (
              <span key={`${f.entity_id}-${f.periodo}`} className="code mr-2">
                {f.periodo}
              </span>
            ))}
            {q.data!.faltantes.length > 18 ? `y ${q.data!.faltantes.length - 18} más` : null}
          </p>
        </WorkPanel>
      ) : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && (q.data?.arrastres.length ?? 0) === 0}
        emptyMessage={t("No hay ningún saldo arrastrado declarado.")}
        emptyHint={t("Copia las casillas de la declaración del mes anterior. Si la empresa arranca de cero en este sistema, decláralo igual con importe cero: así el aviso deja de salir.")}
        onRetry={() => void q.refetch()}
        columns={[16, 26, 18, 16, 24]}
      >
        <WorkPanel
          title={t("Declarados")}
          footnote={q.data?.nota}
        >
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t("Período")}</TableHead>
                <TableHead className="w-[16rem]">{t("Concepto")}</TableHead>
                <TableHead className="w-32 text-right">{t("Importe")}</TableHead>
                <TableHead className="w-24">{t("Origen")}</TableHead>
                <TableHead>{t("Motivo")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(q.data?.arrastres ?? []).map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="code">{a.periodo}</TableCell>
                  <TableCell className="truncate" title={a.nombre}>
                    {a.nombre}
                  </TableCell>
                  <TableCell
                    className={cn("num", Number(a.importe) === 0 && "num-zero")}
                  >
                    {formatAmount(Number(a.importe))}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={a.origen === "apertura" ? "outline" : "brand-soft"}
                      estado
                    >
                      {a.origen}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="truncate text-2xs text-muted-foreground"
                    title={a.motivo}
                  >
                    {a.motivo}
                    {a.declarado_por ? ` — ${a.declarado_por}` : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>
    </div>
  )
}

function Declarar({
  sociedades,
  conceptos,
}: {
  sociedades: { id: string; razon_social: string; pais: string }[]
  conceptos: { codigo: string; nombre: string; efecto: string; donde: string }[]
}) {
  const [entityId, setEntityId] = React.useState("")
  const [periodo, setPeriodo] = React.useState("")
  const [importes, setImportes] = React.useState<Record<string, string>>({})
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  // Sólo las peruanas: los otros tres rulepacks no generan formularios, así que
  // declarar un arrastre suyo no lo consumiría nadie.
  const peruanas = sociedades.filter((s) => s.pais === "PE")

  const guardar = useMutation({
    mutationFn: () =>
      declararArrastres({
        entity_id: entityId,
        periodo,
        importes: Object.fromEntries(
          conceptos.map((c) => [c.codigo, Number(importes[c.codigo] ?? 0)]),
        ),
        motivo,
      }),
    onSuccess: (r) => {
      toast({
        title: `${r.declarados} saldos declarados para ${r.periodo}`,
        description:
          "Vuelve a generar la declaración de ese período y cotéjala contra la anterior antes de firmar.",
      })
      setImportes({})
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["arrastres"] })
      void qc.invalidateQueries({ queryKey: ["filing-declaracion"] })
    },
    onError: onWriteError("los saldos arrastrados", "declarar"),
  })

  const mesValido = /^\d{4}-(0[1-9]|1[0-2])$/.test(periodo)
  const listo = entityId !== "" && mesValido && motivo.trim().length >= 3
  const todoCero = conceptos.every((c) => Number(importes[c.codigo] ?? 0) === 0)

  return (
    <WorkPanel
      title={t("Declarar los saldos de un período")}
      footnote={t("Se declaran los cinco a la vez porque así llega el dato: alguien tiene delante la declaración del mes anterior y copia sus casillas.")}
      bodyClassName="p-2.5"
    >
      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_10rem]">
        <div className="space-y-1">
          <Label htmlFor="arr-sociedad">{t("Sociedad")}</Label>
          <select
            id="arr-sociedad"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{t("Elige la sociedad…")}</option>
            {peruanas.map((s) => (
              <option key={s.id} value={s.id}>
                {s.razon_social}
              </option>
            ))}
          </select>
          {peruanas.length < sociedades.length ? (
            <p className="text-2xs text-muted-foreground">
              {t("Sólo las peruanas: los rulepacks de México, Panamá y Brasil no generan formularios todavía, así que un arrastre suyo no lo consumiría nadie.")}</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="arr-periodo">{t("Período")}</Label>
          <Input
            id="arr-periodo"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            placeholder="2026-03"
            className="code h-7 text-xs"
            aria-invalid={periodo !== "" && !mesValido}
          />
        </div>
      </div>

      <div className="mt-2.5 space-y-1.5">
        {conceptos.map((c) => (
          <div
            key={c.codigo}
            className="grid gap-1 md:grid-cols-[minmax(0,1fr)_10rem] md:items-center"
          >
            <div>
              <Label htmlFor={`arr-${c.codigo}`} className="text-xs">
                {c.nombre}
              </Label>
              <p className="text-2xs leading-4 text-muted-foreground">{c.efecto}</p>
            </div>
            <Input
              id={`arr-${c.codigo}`}
              value={importes[c.codigo] ?? ""}
              onChange={(e) =>
                setImportes((prev) => ({ ...prev, [c.codigo]: e.target.value }))
              }
              inputMode="decimal"
              placeholder="0.00"
              className="num h-7 text-xs"
            />
          </div>
        ))}
      </div>

      {todoCero ? (
        /*
          TODO EN CERO ES UNA DECLARACION, y hay que decir que lo es. Si la
          empresa de verdad arranca aqui, cero es correcto y se guarda como
          apertura: la advertencia deja de salir. Si es que nadie ha mirado la
          declaracion anterior, esto es peor que no declarar nada, porque apaga
          el aviso sin resolver el dato.
        */
        <p className="mt-2 flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning-soft/40 px-2.5 py-2 text-2xs leading-4">
          <TriangleAlert className="mt-px h-3 w-3 shrink-0 text-warning" />
          <span>
            {t("Están los cinco en cero. Se va a guardar como")}{" "}
            <strong className="font-medium">apertura</strong>{t(": una afirmación de que aquí no venía nada, y el aviso de esas casillas dejará de salir. Hazlo sólo si de verdad es el primer período de esta empresa — si es que todavía nadie miró la declaración anterior, apagas el aviso sin resolver el dato.")}</span>
        </p>
      ) : null}

      <div className="mt-2 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="space-y-1">
          <Label htmlFor="arr-motivo">{t("Motivo *")}</Label>
          <Textarea
            id="arr-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="text-xs"
            placeholder={t("De dónde salen estas cifras. «Casillas 145, 165 y 179 de la declaración de febrero», por ejemplo.")}
          />
        </div>
        <Button size="sm" disabled={!listo || guardar.isPending} onClick={() => guardar.mutate()}>
          <Coins />
          {guardar.isPending ? "Guardando…" : "Declarar"}
        </Button>
      </div>
    </WorkPanel>
  )
}
